import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { warmAuthSession, refreshAuthSession, resetAuthSessionWarm } from './sessionWarm';

// Resolve the Cognito Identity Pool identityId, auto-recovering once from a
// stale cached identity. The identityId only powers S3 thumbnail/attachment
// paths -- it is deliberately decoupled from "is the user signed in", which is
// determined by getCurrentUser()/fetchUserAttributes() in updateUser below.
//
// Both steps go through sessionWarm.js. The first attempt piggybacks on the
// warm WarmedOutlet already awaited (zero extra Cognito calls in the common
// case), and the destructive clear+forceRefresh recovery is PUBLISHED as the
// shared warm promise -- so page fetches asking for a session mid-recovery
// join it instead of racing the freshly-wiped credentials cache. (The old
// private clearCredentials()+forceRefresh here ran concurrently with page
// mounts and wiped the cache underneath their in-flight GraphQL calls.)
//
// Returns the identityId, or throws if it genuinely can't be resolved after a
// clean retry (a real identity-pool problem worth surfacing).
async function fetchIdentityIdWithRecovery() {
  let identityId;
  try {
    identityId = (await warmAuthSession()).identityId;
  } catch (_) {
    // Swallow here; the recovery path below re-attempts and will rethrow if it
    // also fails, so genuine errors still surface to the caller.
    identityId = undefined;
  }
  if (identityId) return identityId;

  // First attempt produced no identityId (threw, or returned empty). The usual
  // cause is a stale cached identity rejecting the current token: clear it and
  // force a fresh GetId / GetCredentialsForIdentity round-trip.
  return (await refreshAuthSession()).identityId;
}

// Amplify's name for "there is no signed-in user" -- thrown by assertAuthTokens
// out of getCurrentUser() / fetchUserAttributes() when the token store is
// empty. It is a STATE, not a failure: RequireAuth renders the Authenticator's
// sign-in form for exactly this case, so the user is already being asked to do
// the one thing that fixes it. Keyed on `name` rather than the message, which
// is prose and not part of the contract.
const USER_UNAUTHENTICATED_EXCEPTION = 'UserUnAuthenticatedException';

function isNotSignedIn(err) {
  return err?.name === USER_UNAUTHENTICATED_EXCEPTION;
}

// Whether an auth problem on this page is worth an error banner. Only the
// authed app (/me/*) is: on the public routes (/, /support, /salish, the
// shared-trip view, ...) an anonymous visitor is the NORMAL case, and
// getCurrentUser() throwing UserUnAuthenticatedException there is not news.
// The old check exempted only '/', which put a spurious error on every other
// public page for signed-out visitors.
function requiresAuth(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/me');
}

// Builds the callback we hand to Hub.listen('auth', ...) (and call once at
// startup). It keeps App's `user` state in sync with the Cognito session.
//
// Dependencies are injected so the handler is testable without rendering the
// whole App (which would pull in the Amplify <Authenticator> and the entire
// pages barrel):
//   - setUser, setIsInitializing, hideStatus, showError: App state setters.
//   - redirectHome():  navigate to the public home page ('/').
//   - getPathname():   current window.location.pathname.
export function createAuthStateHandler({
  setUser,
  setIsInitializing,
  hideStatus,
  showError,
  redirectHome,
  getPathname,
}) {
  return async function updateUser(capsule) {
    // When the user signs out, Amplify fires a 'signedOut' Hub event. Don't
    // try to refetch the user here -- getCurrentUser()/fetchUserAttributes()
    // would throw UserUnAuthenticatedException and surface a spurious error
    // ("User needs to be authenticated to call this API") on whatever page
    // they signed out from. Instead, clear the user and send them to the
    // public home page.
    if (capsule?.payload?.event === 'signedOut') {
      // Forget the warmed session so a later sign-in (possibly a different
      // user in the same tab) starts from a fresh fetch.
      resetAuthSessionWarm();
      setUser(null);
      hideStatus();
      setIsInitializing(false);
      if (getPathname() !== '/') {
        redirectHome();
      }
      return;
    }

    setIsInitializing(true);

    // I was doing Promise.all here to do all these in parallel. I periodically
    // would get a TooManyRequestsException: Rate exceeded. I think it was
    // because I was doing the requests in parallel. I'm not sure if that's the
    // cause, but I'm going to do them in series now.
    try {
      let user = await getCurrentUser();
      let attributes = await fetchUserAttributes();
      user.email = attributes.email;
      user.email_verified = attributes.email_verified;

      // identityId is best-effort and decoupled from being signed in: a stale
      // cached Cognito identity can make this fail even when the user-pool
      // session is perfectly valid. Recover automatically, and never let an
      // identity-pool hiccup sign the user out.
      let identityError = null;
      try {
        user.identityId = await fetchIdentityIdWithRecovery();
      } catch (err) {
        console.error('Error resolving Cognito identityId: ', err);
        user.identityId = undefined;
        identityError = err;
      }

      // The user IS authenticated (user-pool session succeeded above), so keep
      // them signed in even if the identity pool couldn't mint an identity.
      setUser(user);

      if (identityError && requiresAuth(getPathname())) {
        // Auto-recovery didn't help -- a real identity-pool problem. Surface it
        // rather than degrading silently, but the app stays usable (only S3
        // thumbnails/attachments are affected).
        showError(identityError);
      } else {
        hideStatus();
      }
    } catch (err) {
      if (isNotSignedIn(err)) {
        // Not an error: no session, and the Authenticator is already showing
        // the sign-in form. Landing on /me/* signed out is an ordinary way in
        // -- a bookmark, or a session that lapsed while the tab was closed --
        // and a red banner over the sign-in form only reads as a broken app.
        // Clear any banner left over from a previous state.
        hideStatus();
      } else {
        console.error('Error getting user: ', err);
        // A genuine failure (network, service, misconfiguration). Surface it
        // only inside the authed app, where the user expected to get in.
        if (requiresAuth(getPathname())) {
          showError(err);
        }
      }
      setUser(null);
    } finally {
      // Done initializing.
      setIsInitializing(false);
    }
  };
}
